<script setup lang="ts">
import { useDebounceFn, useEventBus } from "@vueuse/core";
import equal from "deep-equal";
import { GridItem, GridLayout, type LayoutItem } from "grid-layout-plus";
import { capitalize, computed, onUnmounted, ref, toRaw, watch } from "vue";

import { EVENT_BUS_TASK_UPDATED_WS } from "~/actions/websockets";
import { backendOld } from "~/api";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import { colorsByTheme } from "~/constants/style";
import { PlusIcon } from "~/icons";
import {
  ChartAggregation,
  DropdownMenuItemKind,
  PieChartDisplayMetric,
  Placement,
  StatisticType,
} from "~/shared/enums";
import type { ChartAdtl, ChartData, Dashboard, StatisticConfig } from "~/shared/types";
import { useDataStore, usePageStore } from "~/stores";
import { asyncState } from "~/utils/async";
import { makeDuid } from "~/utils/common";
import { when } from "~/utils/wait";

import { getStatisticDefinition, STATISTIC_DEFINITIONS } from "./constants";
import TaskStatisticItem from "./TaskStatisticItem.vue";

type StatisticItem = LayoutItem & { duid: string; config: StatisticConfig };

const COL_NUM = 12;
const ADD_CHART_WIDTH = 2;
const ADD_CHART_HEIGHT = 1;

const props = defineProps<{
  dashboard: Dashboard;
}>();

const emit = defineEmits<{
  update: [configs: StatisticConfig[], loadData: boolean];
}>();

const dataStore = useDataStore();
const pageStore = usePageStore();

const colors = computed(() => colorsByTheme[pageStore.theme]);

const statistics = computed<StatisticConfig[]>(() => props.dashboard.charts);
const {
  state: dashboardData,
  isLoading: isLoadingData,
  execute: executeDataLoad,
} = asyncState<Map<string, ChartData>>(
  async () => {
    const res = await backendOld.dashboards.getChartData(props.dashboard.duid);
    return new Map(Object.entries(res.data.results));
  },
  {
    watch: () => props.dashboard.duid,
    default: new Map(),
    preserveState: true,
  }
);
const showLoadingForDuids = ref<Set<string>>(new Set(statistics.value.map((e) => e.duid)));
watch(isLoadingData, (isLoading) => {
  if (isLoading) {
    return;
  }
  // Reset the set when data is loaded
  showLoadingForDuids.value = new Set();
});

const loadChartData = (showLoading: boolean = false) => {
  if (showLoading) {
    showLoadingForDuids.value = new Set(statistics.value.map((e) => e.duid));
  }
  return executeDataLoad();
};

// Reload the data when a task is updated
const throttledReload = useDebounceFn(executeDataLoad, 10000, { maxWait: 10000 });
const progressReportBus = useEventBus(EVENT_BUS_TASK_UPDATED_WS);
const unsubscribe = progressReportBus.on(throttledReload);
onUnmounted(unsubscribe);

const statisticsToLayout = (stats: StatisticConfig[]): StatisticItem[] =>
  stats.map((e) => {
    const definition = getStatisticDefinition(e.type);
    return {
      duid: e.duid,
      i: e.duid,
      x: e.x,
      y: e.y,
      w: e.width,
      h: e.height,
      minW: definition?.sizes?.minW,
      minH: definition?.sizes?.minH,
      maxW: definition?.sizes?.maxW,
      maxH: definition?.sizes?.maxH,
      moved: false,
      config: e,
    };
  });

const grid = ref<InstanceType<typeof GridLayout> | null>(null);
const editing = computed(() => grid.value?.state.isDragging);
const layout = ref<StatisticItem[]>(statisticsToLayout(statistics.value));

// The library has a bug where it needs to be initialized with same data once it's ready in order for the drag/resize to work
let initialized = false;
when(
  () => !!grid.value && grid.value.state.width > 0 && !initialized,
  () => {
    layout.value = statisticsToLayout(statistics.value);
    initialized = true;
  }
);

watch(
  () => statistics.value,
  (newValue) => {
    const newLayout = statisticsToLayout(newValue);
    if (
      !equal(
        toRaw(layout.value).map((e) => ({ ...e, config: { ...e.config, x: e.x, y: e.y, width: e.w, height: e.h } })),
        toRaw(newLayout),
        { strict: true }
      )
    ) {
      layout.value = newLayout;
    }
  }
);

const addChartPosition = computed(() => {
  const furthestY = layout.value.reduce((acc, item) => Math.max(acc, item.y + item.h), 0);
  return { x: 0, y: furthestY };
});

const getDefaultAdtlData = (type: StatisticType): ChartAdtl => {
  switch (type) {
    case StatisticType.BAR: {
      return {
        xPropertyDuid: dataStore.defaultAssigneesProperty.duid,
        stackPropertyDuid: null,
        aggregationPropertyDuid: null,
        aggregation: ChartAggregation.COUNT,
        showEmptyValues: false,
      };
    }
    case StatisticType.BURN_UP: {
      return {
        startDate: null,
        endDate: null,
      };
    }
    case StatisticType.LINE: {
      return {
        xPropertyDuid: dataStore.defaultAssigneesProperty.duid,
        groupByPropertyDuid: dataStore.defaultStatusProperty.duid,
        showEmptyValues: false,
      };
    }
    case StatisticType.NUMBER: {
      return {
        propertyDuid: null,
        aggregation: ChartAggregation.COUNT,
      };
    }
    case StatisticType.PIE: {
      return {
        propertyDuid: dataStore.defaultAssigneesProperty.duid,
        displayMetric: PieChartDisplayMetric.COUNT,
        showEmptyValues: false,
      };
    }
    case StatisticType.TABLE: {
      return {
        columnPropertyDuid: dataStore.defaultAssigneesProperty.duid,
        rowPropertyDuid: dataStore.defaultStatusProperty.duid,
        aggregationPropertyDuid: null,
        aggregation: ChartAggregation.COUNT,
        showEmptyValues: false,
      };
    }
    case StatisticType.TEXT_BLOCK:
    default: {
      return {
        description: "",
      };
    }
  }
};

const onAddConfig = (type: StatisticType) => {
  const definition = getStatisticDefinition(type);
  if (!definition) {
    return;
  }

  const newConfig = {
    duid: makeDuid(),
    type,
    title: "Chart",
    x: addChartPosition.value.x,
    y: addChartPosition.value.y,
    width: definition.sizes?.defaultW ?? 6,
    height: definition.sizes?.defaultH ?? 4,
    adtl: getDefaultAdtlData(type),
  } as StatisticConfig;
  newConfig.title = definition.getDefaultTitle(newConfig);

  showLoadingForDuids.value.add(newConfig.duid);
  emit("update", statistics.value.concat([newConfig]), true);
};

const onUpdateConfig = (config: StatisticConfig) => {
  emit(
    "update",
    statistics.value.map((e) =>
      e.duid === config.duid ? { ...config, x: e.x, y: e.y, width: e.width, height: e.height } : e
    ),
    true
  );
  showLoadingForDuids.value.add(config.duid);
};

const onDeleteConfig = (config: StatisticConfig) => {
  emit(
    "update",
    statistics.value.filter((e) => e.duid !== config.duid),
    false
  );
};

const onLayoutChanged = () => {
  emit(
    "update",
    statistics.value.map((e) => {
      const item = layout.value.find((i) => i.duid === e.duid);
      return item ? { ...e, x: item.x, y: item.y, width: item.w, height: item.h } : e;
    }),
    false
  );
};

const dropdownSections = computed(() => [
  {
    title: "Add chart",
    items: Object.values(STATISTIC_DEFINITIONS).map(({ type, icon }) => ({
      title: capitalize(type),
      kind: DropdownMenuItemKind.BUTTON,
      icon,
      onClick: () => onAddConfig(type),
    })),
  },
]);

defineExpose({ loadChartData });
</script>

<template>
  <div class="w-full">
    <GridLayout
      ref="grid"
      :layout="layout"
      :col-num="COL_NUM"
      :cols="{ lg: COL_NUM, md: COL_NUM, sm: COL_NUM, xs: COL_NUM / 2, xxs: COL_NUM / 2 }"
      :row-height="40"
      :margin="[16, 16]"
      class="dart-task-statistic"
      is-draggable
      is-resizable
      is-bounded
      responsive
      vertical-compact>
      <div v-if="editing" class="dart-layout-grid absolute inset-4 flex gap-4">
        <div v-for="i in COL_NUM" :key="i" class="box-content size-full border-x border-lt" />
        <div class="absolute inset-0 flex flex-col gap-4 overflow-hidden text-clip">
          <div v-for="i in addChartPosition.y + 100" :key="i" class="h-[40px] w-full shrink-0 border-y border-lt" />
        </div>
      </div>
      <GridItem
        v-for="item in layout"
        :key="item.duid"
        v-bind="item"
        class="[&>span]:hover:!block"
        drag-allow-from=".dart-drag-handle"
        @moved="() => onLayoutChanged()"
        @resized="() => onLayoutChanged()">
        <TaskStatisticItem
          :config="item.config"
          :data="dashboardData.get(item.config.duid)"
          :is-loading="isLoadingData && showLoadingForDuids.has(item.config.duid)"
          @update="onUpdateConfig"
          @delete="onDeleteConfig" />
      </GridItem>
      <GridItem
        v-if="!editing && !isLoadingData"
        i="add"
        :x="addChartPosition.x"
        :y="addChartPosition.y"
        :w="ADD_CHART_WIDTH"
        :h="ADD_CHART_HEIGHT"
        static>
        <DropdownMenu
          class="size-full select-none rounded bg-std border-md hover:bg-lt"
          :sections="dropdownSections"
          :distance="2"
          :placement="Placement.RIGHT_TOP">
          <button type="button" class="flex size-full items-center justify-center gap-2">
            <PlusIcon class="text-md icon-lg" />
            <span class="font-medium text-md">Add chart</span>
          </button>
        </DropdownMenu>
      </GridItem>
    </GridLayout>
  </div>
</template>

<style scoped>
.vgl-layout {
  @apply rounded-lg bg-std;
  --vgl-resizer-size: 12px;
  --vgl-resizer-border-color: v-bind("colors.borderMd");
  --vgl-resizer-border-width: 2px;
}

:deep(.vgl-item) {
  @apply !transition-none;
}

:deep(.vgl-item--placeholder) {
  @apply rounded-lg bg-primary-base opacity-60;
}

:deep(.vgl-item--resizing) {
  @apply opacity-80;
}

:deep(.vgl-item__resizer) {
  @apply m-2 hidden;
}

.dart-task-statistic {
  :deep(.apexcharts-gridline) {
    box-shadow: 0 0 1px transparent !important;
  }
  :deep(.apexcharts-tooltip) {
    @apply min-w-52 !rounded-xl border !bg-white p-3 !shadow border-md dark:!bg-zinc-850;
  }
  :deep(.apexcharts-tooltip-title) {
    @apply !mb-2 !border-none !bg-transparent !p-0 text-sm font-semibold text-hvy;
  }
  :deep(.apexcharts-tooltip-y-group) {
    @apply flex w-full items-center gap-1 !py-1;
  }
  :deep(.apexcharts-tooltip-text-y-label) {
    @apply w-full flex-1 text-xs font-medium leading-tight text-md;
  }
  :deep(.apexcharts-tooltip-text-y-value) {
    @apply !m-0 font-semibold leading-tight text-lt;
  }
}
</style>
